// My day and one search across every app: the pure part (no React, no network), so the rules are tested.
// The database does the work (nateva-platform migrations 0100 and 0101: core.my_day, core.everyone_day,
// core.search_all) and has already applied each app's access rules. This file only checks the shape of what
// came back, groups it, words it and makes sure a link goes somewhere safe.

export type Bucket = 'overdue' | 'today' | 'week' | 'month' | 'now'
export type Tone = 'late' | 'soon' | 'later' | 'info'

export const BUCKETS: Bucket[] = ['overdue', 'today', 'week', 'month', 'now']

export type DayItem = {
  app: string
  kind: string
  title: string
  detail: string | null
  due_on: string | null
  bucket: Bucket
  tone: Tone
  link: string | null
  who: string | null
  mine: boolean
}

export type Counts = Record<Bucket, number>
export type LegalCounts = Omit<Counts, 'now'>

export type MyDay = {
  scope: 'mine' | 'all'
  asOf: string
  sample: boolean
  person: { name: string | null; email: string | null }
  apps: string[]
  appUrls: Record<string, string>
  counts: Counts
  allContracts: LegalCounts | null
  total: number
  items: DayItem[]
}

export type EveryoneDay = {
  allowed: boolean
  asOf: string
  sample: boolean
  people: { name: string; counts: Counts; items: DayItem[] }[]
}

export const APP_NAME: Record<string, string> = {
  agreements: 'Agreements',
  partners: 'Partners',
  field: 'Field',
  yield: 'Yield',
  carbon: 'Carbon',
  estate: 'Estate',
  programme: 'Programme',
  reports: 'Reports',
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null)
const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0)

function counts(v: unknown): Counts {
  const o = isObj(v) ? v : {}
  return { overdue: num(o.overdue), today: num(o.today), week: num(o.week), month: num(o.month), now: num(o.now) }
}

export function toItem(v: unknown): DayItem | null {
  if (!isObj(v)) return null
  const bucket = BUCKETS.find((b) => b === v.bucket)
  const title = str(v.title)
  if (!bucket || !title) return null
  const tone = (['late', 'soon', 'later', 'info'] as const).find((t) => t === v.tone) ?? 'info'
  return {
    app: str(v.app) ?? 'programme',
    kind: str(v.kind) ?? 'To do',
    title,
    detail: str(v.detail),
    due_on: str(v.due_on),
    bucket,
    tone,
    link: str(v.link),
    who: str(v.who),
    mine: v.mine === true,
  }
}

const items = (v: unknown): DayItem[] => (Array.isArray(v) ? v.map(toItem).filter((x): x is DayItem => x !== null) : [])

/** The answer of core.my_day, or null when the person is not signed in or the answer is not the shape we expect. */
export function parseMyDay(raw: unknown): MyDay | null {
  if (!isObj(raw) || raw.signed_in !== true) return null
  const person = isObj(raw.person) ? raw.person : {}
  const legal = isObj(raw.all_contracts) ? raw.all_contracts : null
  const urls = isObj(raw.app_urls) ? raw.app_urls : {}
  return {
    scope: raw.scope === 'all' ? 'all' : 'mine',
    asOf: str(raw.as_of) ?? '',
    sample: raw.sample !== false,
    person: { name: str(person.name), email: str(person.email) },
    apps: Array.isArray(raw.apps) ? raw.apps.filter((a): a is string => typeof a === 'string') : [],
    appUrls: Object.fromEntries(Object.entries(urls).filter((e): e is [string, string] => typeof e[1] === 'string')),
    counts: counts(raw.counts),
    allContracts: legal ? (({ now: _now, ...rest }) => rest)(counts(legal)) : null,
    total: num(raw.total),
    items: items(raw.items),
  }
}

export function parseEveryone(raw: unknown): EveryoneDay | null {
  if (!isObj(raw)) return null
  const people = Array.isArray(raw.people) ? raw.people : []
  return {
    allowed: raw.allowed === true,
    asOf: str(raw.as_of) ?? '',
    sample: raw.sample !== false,
    people: people.flatMap((p) => {
      if (!isObj(p) || !str(p.name)) return []
      return [{ name: String(p.name), counts: counts(p), items: items(p.items) }]
    }),
  }
}

export function groupByBucket(list: DayItem[]): Record<Bucket, DayItem[]> {
  const out: Record<Bucket, DayItem[]> = { overdue: [], today: [], week: [], month: [], now: [] }
  for (const i of list) out[i.bucket].push(i)
  return out
}

// ---- Words ----------------------------------------------------------------------------------------------

const DAY_MS = 86_400_000
const parse = (iso: string): number => Date.parse(`${iso}T12:00:00Z`)

export function daysFrom(asOf: string, due: string): number {
  return Math.round((parse(due) - parse(asOf)) / DAY_MS)
}

const WEEKDAY = new Intl.DateTimeFormat('en-NZ', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })

/** "3 days late", "Today", "Tomorrow", "Fri 7 Nov". An item with no date says what it is. */
export function dueLabel(item: Pick<DayItem, 'due_on' | 'bucket'>, asOf: string): string {
  if (!item.due_on) return ''
  const d = daysFrom(asOf, item.due_on)
  if (d < 0) return d === -1 ? '1 day late' : `${-d} days late`
  if (d === 0) return 'Today'
  if (d === 1) return 'Tomorrow'
  return WEEKDAY.format(new Date(parse(item.due_on)))
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

/** One plain sentence on how the day looks. */
export function headline(c: Counts): string {
  const late = c.overdue
  const today = c.today
  if (late === 0 && today === 0) {
    return c.week > 0 ? `Nothing is due today. ${plural(c.week, 'thing')} due in the next 7 days.` : 'Nothing is due today or this week.'
  }
  const parts: string[] = []
  if (late > 0) parts.push(`${plural(late, 'thing')} overdue`)
  if (today > 0) parts.push(`${plural(today, 'thing')} due today`)
  return `${parts.join(' and ')}.`
}

// ---- Links ----------------------------------------------------------------------------------------------

export type Dest = { href: string; external: boolean }

/** Where a link goes. The database writes either a path inside the front door ("/home?project=x") or the
 * address of one of Nateva's own apps. Anything else (another site, a script) goes nowhere. */
export function resolveLink(link: string | null | undefined, domain: string): Dest | null {
  if (!link) return null
  if (link.startsWith('/') && !link.startsWith('//')) return { href: link, external: false }
  try {
    const u = new URL(link)
    const host = u.hostname.toLowerCase()
    if (u.protocol === 'https:' && (host === domain || host.endsWith(`.${domain}`))) return { href: u.toString(), external: true }
  } catch {
    // not an address
  }
  return null
}

// ---- Search ---------------------------------------------------------------------------------------------

export type SearchHit = {
  kind: string
  label: string
  app: string
  title: string
  sub: string
  link: string
  rank: number
}

export function parseHits(raw: unknown): SearchHit[] {
  if (!Array.isArray(raw)) return []
  return raw.flatMap((r) => {
    if (!isObj(r)) return []
    const title = str(r.title)
    const link = str(r.link)
    if (!title || !link) return []
    return [{ kind: str(r.kind) ?? 'result', label: str(r.label) ?? 'Result', app: str(r.app) ?? '', title, sub: str(r.sub) ?? '', link, rank: num(r.rank) }]
  })
}

/** What a hit is called on the row: its kind and, when it is in an app, which app. */
export function hitTag(h: Pick<SearchHit, 'label' | 'app'>): string {
  const app = APP_NAME[h.app]
  return app && !app.toLowerCase().startsWith(h.label.toLowerCase()) ? `${h.label} · ${app}` : h.label
}

/** Search words that are worth sending: trimmed, at least two characters. */
export function searchWords(q: string): string | null {
  const t = q.trim().replace(/\s+/g, ' ')
  return t.length >= 2 ? t.slice(0, 120) : null
}
